/**
 * BPMList normalisation (W1-07a formats-json-rpe; DESIGN.md §7.2, rpe-format.md F§3.2).
 *
 *  - items with an invalid beat (c = 0, negative or fractional parts) or a BPM that is not a positive finite number
 *    are dropped (Phira computes infinite or backwards times with them): 'changed';
 *  - an empty list makes Phira panic at the first time lookup: one 120 BPM point at beat 0 is used, 'changed',
 *    Phira would not load the file;
 *  - an unsorted list is stable-sorted: 'normalized' when every beat used in the chart maps to the same seconds as
 *    in Phira's literal file-order walk, else 'changed';
 *  - of several points at the same beat only the last one takes effect in Phira (its walk adds no time between
 *    them and looks up the last point at or before a beat): the earlier ones are removed, 'normalized';
 *  - a first point after (or before) beat 0 maps that beat to 0 s in Phira; every beat of the chart is shifted by
 *    the first point's beat, which keeps every time identical: 'normalized', `beatShift` = that beat. A later
 *    point whose shifted beat a triple cannot hold (beyond i32, normRange.ts) is removed: 'changed' (only items
 *    that are themselves out of range follow it, and those are refused where they are normalised).
 *
 * The event analysis runs in SOURCE beats on the sorted, validated list (`sec`); output triples go through
 * `out`, which applies the shift (raw, possibly non-normalised triples are kept when there is no shift).
 */

import type { IrBpm } from '../../core/ir/types';
import { Beats } from '../../core/time/beat';
import { createTempoMap } from '../../core/time/tempo';
import type { Beat, Triple } from '../../core/time/types';
import type { NonEmpty } from '../../core/util/types';
import type { RpeBpmItem } from './json';
import { beatLabel, shiftedBeat } from './normRange';
import type { NormReport } from './normReport';

export interface TempoNorm {
  readonly bpm: NonEmpty<IrBpm>;
  /** Beat subtracted from every beat of the chart (null when the first BPM point is at beat 0). */
  readonly shift: Beat | null;
  /** Source beat at chart time 0. */
  readonly zeroBeat: Beat;
  /** Phira seconds of a valid source triple. */
  sec(t: Triple): number;
  /**
   * Output triple of a source triple with exact beat `beat`. Only for beats whose output fits (normRange.ts
   * `fitsShifted`): every item and every beat normalisation adds is checked first.
   */
  out(t: Triple, beat: Beat): Triple;
}

interface ValidBpm {
  /** Index in the source BPMList (report paths). */
  readonly idx: number;
  readonly bpm: number;
  readonly triple: Triple;
  readonly beat: Beat;
  readonly extra: RpeBpmItem['extra'];
}

/** Phira's literal BpmList (file order, no sort): seconds of a beat value (F§3.2). */
export function phiraSecondsLiteral(
  list: ReadonlyArray<{ readonly bpm: number; readonly beat: number }>,
): (x: number) => number {
  const els: { readonly b: number; readonly t: number; readonly bpm: number }[] = [];
  for (const item of list) {
    const prev = els[els.length - 1];
    els.push({
      b: item.beat,
      t: prev === undefined ? 0 : prev.t + (item.beat - prev.b) * (60 / prev.bpm),
      bpm: item.bpm,
    });
  }
  return (x: number): number => {
    let k = 0;
    for (let i = 0; i < els.length; i++) {
      const el = els[i];
      if (el === undefined || el.b > x) break;
      k = i;
    }
    const el = els[k];
    return el === undefined ? x : el.t + (x - el.b) * (60 / el.bpm);
  };
}

/**
 * Normalises the BPM list. `chartBeats` yields every beat triple used by the chart (events, notes), for the
 * unsorted-list identity check.
 */
export function normalizeBpm(
  list: readonly RpeBpmItem[],
  chartBeats: () => Iterable<Triple>,
  report: NormReport,
): TempoNorm {
  const valid: ValidBpm[] = [];
  list.forEach((item, i) => {
    const b = Beats.tryFromTriple(item.startTime);
    if (b === null) {
      report.add('changed', { key: 'rpe.report.bpmInvalidTime' }, `BPMList[${String(i)}].startTime`);
      return;
    }
    if (!(Number.isFinite(item.bpm) && item.bpm > 0)) {
      report.add(
        'changed',
        { key: 'rpe.report.bpmInvalid', params: { bpm: String(item.bpm) } },
        `BPMList[${String(i)}].bpm`,
      );
      return;
    }
    valid.push({ idx: i, bpm: item.bpm, triple: item.startTime, beat: b.beat, extra: item.extra });
  });
  if (valid.length === 0) {
    if (list.length === 0) report.phiraWouldNotLoad();
    report.add('changed', { key: 'rpe.report.bpmEmpty' }, 'BPMList');
    valid.push({ idx: 0, bpm: 120, triple: [0, 0, 1], beat: Beats.ZERO, extra: [] });
  }
  let sorted = valid;
  let unsorted = false;
  for (let i = 1; i < valid.length; i++) {
    const a = valid[i - 1];
    const b = valid[i];
    if (a !== undefined && b !== undefined && Beats.lt(b.beat, a.beat)) unsorted = true;
  }
  if (unsorted) {
    sorted = valid
      .map((v, i) => [v, i] as const)
      .sort((x, y) => Beats.cmp(x[0].beat, y[0].beat) || x[1] - y[1])
      .map(([v]) => v);
  }
  // Points at one beat: Phira's walk takes the last point at or before a beat and adds no time between points at the
  // same beat, so only the last of them (in list order, which the stable sort keeps) ever takes effect. The others
  // are removed, render-identical: 'normalized' (Lift could hold only one of them, and kept the first).
  let duplicates = 0;
  let duplicatePath: string | undefined;
  const distinct: ValidBpm[] = [];
  for (const v of sorted) {
    const prev = distinct[distinct.length - 1];
    if (prev !== undefined && Beats.eq(prev.beat, v.beat)) {
      duplicates++;
      duplicatePath ??= `BPMList[${String(prev.idx)}]`;
      distinct[distinct.length - 1] = v;
    } else distinct.push(v);
  }
  if (duplicates > 0) {
    report.add('normalized', { key: 'rpe.report.bpmDuplicate' }, duplicatePath, undefined, duplicates);
    sorted = distinct;
  }
  const map = createTempoMap(sorted.map((v) => ({ bpm: v.bpm, startTime: v.triple })));
  if (unsorted) {
    const literal = phiraSecondsLiteral(valid.map((v) => ({ bpm: v.bpm, beat: Beats.tripleToF64(v.triple) })));
    let same = true;
    for (const t of chartBeats()) {
      if (Beats.tryFromTriple(t) === null) continue;
      const x = Beats.tripleToF64(t);
      if (literal(x) !== map.secAtBeatF64(x)) {
        same = false;
        break;
      }
    }
    report.add(
      same ? 'normalized' : 'changed',
      same ? { key: 'rpe.report.bpmUnsorted' } : { key: 'rpe.report.bpmUnsortedChanged' },
      'BPMList',
    );
  }
  const first = sorted[0] ?? { idx: 0, bpm: 120, triple: [0, 0, 1] as Triple, beat: Beats.ZERO, extra: [] };
  const shift = Beats.eq(first.beat, Beats.ZERO) ? null : first.beat;
  if (shift !== null) {
    report.add(
      'normalized',
      { key: 'rpe.report.bpmShifted', params: { beat: beatLabel(shift) } },
      'BPMList[0].startTime',
    );
  }
  const out = (t: Triple, beat: Beat): Triple => {
    if (shift === null) return t;
    // Callers only pass beats that fit (checked with `fitsShifted`); the rounded difference is never reached.
    return Beats.toTriple(shiftedBeat(beat, shift) ?? Beats.sub(beat, shift));
  };
  const bpm: IrBpm[] = [];
  let outOfRange = 0;
  let outOfRangePath: string | undefined;
  for (const v of sorted) {
    if (shiftedBeat(v.beat, shift) === null) {
      outOfRange++;
      outOfRangePath ??= `BPMList[${String(v.idx)}].startTime`;
      continue;
    }
    bpm.push({ bpm: v.bpm, startTime: out(v.triple, v.beat), extra: v.extra });
  }
  if (outOfRange > 0) {
    report.add('changed', { key: 'rpe.report.bpmBeatOutOfRange' }, outOfRangePath, undefined, outOfRange);
  }
  const head = bpm[0] ?? { bpm: 120, startTime: [0, 0, 1] as Triple, extra: [] };
  return {
    bpm: [head, ...bpm.slice(1)],
    shift,
    zeroBeat: first.beat,
    sec: (t: Triple): number => map.secAtTriple(t),
    out,
  };
}
